import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Calendar as CalendarIcon, Plus, X, Printer, ArrowLeft, ArrowRight, CopySimple, Storefront } from "@phosphor-icons/react";
import { groceryFromWeek, groceryCustom, listRecipes, getGroceryStores, getGroceryRoute } from "../lib/api";

function mondayOf(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}
function shift(iso, delta) {
  const d = new Date(iso); d.setDate(d.getDate() + delta); return d.toISOString().slice(0, 10);
}

export default function Grocery() {
  const [mode, setMode] = useState("week"); // week | custom
  const [start, setStart] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [multiplier, setMultiplier] = useState(1);
  const [items, setItems] = useState([]);
  const [recipeCount, setRecipeCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [selected, setSelected] = useState([]); // {recipe_id, servings_eaten}
  const [store, setStore] = useState("us_supermarket");
  const [stores, setStores] = useState([]);
  const [route, setRoute] = useState(null);

  useEffect(() => { getGroceryStores().then(d => setStores(d.stores || [])); }, []);
  useEffect(() => { listRecipes().then(d => setRecipes(d.recipes || [])); }, []);

  // Compute smart route whenever items or store changes
  useEffect(() => {
    if (items.length === 0) { setRoute(null); return; }
    getGroceryRoute(items, store).then(setRoute).catch(() => {});
  }, [items, store]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "week") {
        const d = await groceryFromWeek(start, Number(multiplier) || 1);
        setItems(d.items || []); setRecipeCount(d.recipe_count || 0);
      } else {
        const d = await groceryCustom(selected);
        setItems(d.items || []); setRecipeCount(d.recipe_count || 0);
      }
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [mode, start, multiplier, selected]);

  useEffect(() => { load(); }, [load]);

  const addToCustom = (id) => {
    if (selected.find(s => s.recipe_id === id)) return;
    setSelected(prev => [...prev, { recipe_id: id, servings_eaten: 1 }]);
  };
  const removeCustom = (id) => setSelected(prev => prev.filter(s => s.recipe_id !== id));
  const updCustom = (id, srv) => setSelected(prev => prev.map(s => s.recipe_id === id ? { ...s, servings_eaten: Number(srv) || 1 } : s));

  // group by category for display
  const grouped = items.reduce((acc, it) => {
    (acc[it.category] ||= []).push(it); return acc;
  }, {});

  const copyList = async () => {
    const text = items.map(it => `- ${it.name} — ${it.grams} g`).join("\n");
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="grocery-page">
      <div className="pb-6 border-b border-border flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Shopping list</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Grocery</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">Roll up a week of logged meals — or hand-pick recipes and servings — into one shopping list. Quantities merge automatically per ingredient.</p>
        </div>
        <div className="border border-border flex" data-testid="mode-toggle">
          <button onClick={() => setMode("week")} data-testid="grocery-mode-week"
            className={`h-10 px-4 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 ${mode === "week" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <CalendarIcon size={14} /> From week
          </button>
          <button onClick={() => setMode("custom")} data-testid="grocery-mode-custom"
            className={`h-10 px-4 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 ${mode === "custom" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <ShoppingCart size={14} /> Custom
          </button>
        </div>
      </div>

      {mode === "week" ? (
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-px bg-border border border-border" data-testid="week-controls">
          <div className="bg-card p-3 flex items-center gap-2">
            <button onClick={() => setStart(shift(start, -7))} data-testid="week-prev"
              className="h-9 w-9 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><ArrowLeft size={14} /></button>
            <input type="date" value={start} onChange={e => setStart(e.target.value)}
              data-testid="week-start"
              className="h-9 px-3 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            <button onClick={() => setStart(shift(start, 7))} data-testid="week-next"
              className="h-9 w-9 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><ArrowRight size={14} /></button>
          </div>
          <div className="bg-card p-3 flex items-center font-mono text-sm text-muted-foreground">
            Week of {start} · {recipeCount} recipe entries
          </div>
          <div className="bg-card p-3 flex items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Batch ×</label>
            <input type="number" step="0.5" min="0.5" value={multiplier} onChange={e => setMultiplier(e.target.value)}
              data-testid="multiplier"
              className="w-20 h-9 px-2 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="custom-mode">
          <div className="border border-border">
            <div className="px-4 py-3 border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pick recipes</div>
            <div className="max-h-[220px] overflow-y-auto">
              {recipes.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No saved recipes. <Link to="/" className="text-primary underline">Create one</Link>.</div>
              ) : recipes.map(r => (
                <button key={r.id} onClick={() => addToCustom(r.id)} data-testid={`add-recipe-${r.id}`}
                  className="w-full grid grid-cols-[1fr_100px_60px] gap-3 px-4 py-2 border-b border-border last:border-b-0 hover:bg-muted text-left transition-colors duration-200">
                  <div className="truncate">
                    <div className="font-medium truncate">{r.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.ingredients?.length || 0} ings · {r.servings} srv</div>
                  </div>
                  <div className="text-right font-mono text-sm">{Math.round(r.per_serving?.calories || 0)} kcal</div>
                  <div className="flex items-center justify-end">
                    <Plus size={14} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selected.length > 0 && (
            <div className="border border-border" data-testid="custom-selected">
              <div className="px-4 py-3 border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Selected · {selected.length}</div>
              {selected.map(s => {
                const r = recipes.find(x => x.id === s.recipe_id);
                if (!r) return null;
                return (
                  <div key={s.recipe_id} className="grid grid-cols-[1fr_120px_40px] gap-3 items-center px-4 py-2 border-b border-border last:border-b-0" data-testid={`sel-${s.recipe_id}`}>
                    <div className="truncate font-medium">{r.name}</div>
                    <div className="flex items-center gap-2 justify-end">
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Servings</span>
                      <input type="number" min="0.25" step="0.25" value={s.servings_eaten}
                        onChange={e => updCustom(s.recipe_id, e.target.value)}
                        data-testid={`sel-srv-${s.recipe_id}`}
                        className="w-16 h-8 px-2 border border-border bg-background font-mono text-right text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
                    </div>
                    <button onClick={() => removeCustom(s.recipe_id)} data-testid={`rm-${s.recipe_id}`}
                      className="h-8 w-8 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><X size={12} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Grocery output */}
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl tracking-tight">
          {items.length} ingredient{items.length === 1 ? "" : "s"}
        </h2>
        {items.length > 0 && (
          <div className="flex items-center gap-2" data-testid="grocery-actions">
            <button onClick={copyList} data-testid="copy-list"
              className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              <CopySimple size={14} /> Copy
            </button>
            <button onClick={() => window.print()} data-testid="print-list"
              className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              <Printer size={14} /> Print
            </button>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center" data-testid="store-picker">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <Storefront size={14} className="text-primary" /> Smart route for
          </div>
          <select value={store} onChange={e => setStore(e.target.value)} data-testid="store-select"
            className="h-9 px-2 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200">
            {stores.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-right">
            {route ? `${route.total_aisles} aisles · ${route.total_items} items` : ""}
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">Computing…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center" data-testid="empty-grocery">
          <ShoppingCart size={30} className="mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Nothing to shop for yet</div>
          <div className="text-muted-foreground text-sm mt-1">
            {mode === "week" ? "No meals logged in this week — try another date range." : "Pick a recipe above to build your list."}
          </div>
        </div>
      ) : route ? (
        <div className="space-y-4" data-testid="aisle-route">
          {route.route.map((a, ai) => (
            <div key={a.aisle} className="border border-border" data-testid={`aisle-${a.aisle}`}>
              <div className="px-4 py-2 border-b border-border flex items-center justify-between" style={{ borderLeft: `4px solid ${a.color}` }}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Stop {ai + 1}</span>
                  <span className="font-display font-bold text-lg">{a.label}</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{a.items.length} item{a.items.length === 1 ? "" : "s"}</span>
              </div>
              {a.items.map((it) => (
                <div key={it.name} className="grid grid-cols-[16px_1fr_auto_100px] gap-3 items-center px-4 py-2.5 border-b border-border last:border-b-0" data-testid={`route-item-${it.name.replace(/\s+/g, "-")}`}>
                  <div className="h-3 w-3" style={{ background: it.color }} />
                  <div>
                    <div className="capitalize font-medium">{it.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">From {it.recipes.length} recipe{it.recipes.length === 1 ? "" : "s"}</div>
                  </div>
                  <div>
                    {it.availability === "specialty" && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-primary/50 text-primary">Specialty</span>
                    )}
                  </div>
                  <div className="text-right font-mono text-sm font-medium">{it.grams} g</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid="grocery-groups">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="border border-border" data-testid={`group-${cat}`}>
              <div className="px-4 py-2 border-b border-border bg-muted/40 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center justify-between">
                <span>{cat}</span>
                <span>{list.length} item{list.length === 1 ? "" : "s"}</span>
              </div>
              {list.map((it) => (
                <div key={it.name} className="grid grid-cols-[16px_1fr_100px] gap-3 items-center px-4 py-2.5 border-b border-border last:border-b-0" data-testid={`item-${it.name.replace(/\s+/g, "-")}`}>
                  <div className="h-3 w-3" style={{ background: it.color }} />
                  <div>
                    <div className="capitalize font-medium">{it.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">From {it.recipes.length} recipe{it.recipes.length === 1 ? "" : "s"}</div>
                  </div>
                  <div className="text-right font-mono text-sm font-medium">{it.grams} g</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
